// app/(routes)/tools/ai-for-financial-advisors/components/report-ui.tsx
// Presentational primitives shared by the public workflow finder tool, its result
// view, and the shared report page. Marketing tokens (beige family) with the
// structural discipline of the ops surface: quiet panels, tinted icon chips,
// hairline-divided rows, one scarce accent per region.
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

export type ReportTone = "teal" | "green" | "yellow" | "purple" | "neutral";

const CHIP_CLASSES: Record<ReportTone, string> = {
  teal: "bg-secondary/20 text-secondary-dark",
  green: "bg-primary/15 text-primary-dark",
  yellow: "bg-yellow/25 text-yellow-dark",
  purple: "bg-purple/25 text-purple-dark",
  neutral: "bg-text/[0.06] text-text-secondary",
};

/** Faint square grid, the marker of our dark surfaces (see /for/[slug] hero). */
export function DarkGridBackdrop() {
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 opacity-[0.07]"
      style={{
        backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 22px, rgba(255,255,255,0.55) 22px, rgba(255,255,255,0.55) 23px),
          repeating-linear-gradient(90deg, transparent, transparent 22px, rgba(255,255,255,0.55) 22px, rgba(255,255,255,0.55) 23px)`,
      }}
    />
  );
}

export function IconChip({
  icon: Icon,
  tone = "teal",
  size = "md",
  className,
}: {
  icon: LucideIcon;
  tone?: ReportTone;
  size?: "sm" | "md";
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center",
        size === "sm" ? "h-8 w-8 rounded-lg" : "h-11 w-11 rounded-xl",
        CHIP_CLASSES[tone],
        className,
      )}
    >
      <Icon size={size === "sm" ? 15 : 19} aria-hidden="true" />
    </span>
  );
}

export function Eyebrow({
  children,
  icon: Icon,
  className,
}: {
  children: ReactNode;
  icon?: LucideIcon;
  className?: string;
}) {
  return (
    <p
      className={cn(
        "flex items-center gap-2 font-heading text-xs font-black uppercase tracking-[0.18em] text-secondary-dark",
        className,
      )}
    >
      {Icon && <Icon size={14} aria-hidden="true" />}
      {children}
    </p>
  );
}

/** Rank / step marker. Square, not a circle, so it reads as document structure. */
export function RankChip({
  children,
  tone = "dark",
  className,
}: {
  children: ReactNode;
  tone?: "dark" | "teal" | "quiet";
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg font-heading text-xs font-black tabular-nums",
        tone === "dark" && "bg-text text-on-dark",
        tone === "teal" && "bg-secondary/20 text-secondary-dark",
        tone === "quiet" && "bg-text/[0.06] text-text-secondary",
        className,
      )}
    >
      {children}
    </span>
  );
}

export function SectionHead({
  eyebrow,
  eyebrowIcon,
  title,
  description,
  action,
  className,
}: {
  eyebrow?: string;
  eyebrowIcon?: LucideIcon;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between",
        className,
      )}
    >
      <div className="max-w-3xl">
        {eyebrow && <Eyebrow icon={eyebrowIcon}>{eyebrow}</Eyebrow>}
        <h2 className="mt-3 font-heading text-2xl font-black leading-tight text-text sm:text-3xl">
          {title}
        </h2>
        {description && (
          <p className="mt-3 text-sm leading-relaxed text-text-secondary">
            {description}
          </p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/**
 * Quiet container. Surface contrast only: no border, no shadow, no accent.
 * Interactive object cards opt into `shadow-sm` themselves.
 */
export function Panel({
  children,
  padding = "md",
  className,
}: {
  children: ReactNode;
  padding?: "none" | "sm" | "md";
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-2xl bg-surface-light",
        padding === "sm" && "p-5",
        padding === "md" && "p-5 sm:p-7",
        className,
      )}
    >
      {children}
    </div>
  );
}

/** Label-over-value fact, sized for dark hero surfaces. */
export function FactTile({
  icon: Icon,
  label,
  value,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
}) {
  return (
    <div className="rounded-xl bg-on-dark/[0.07] px-4 py-4 ring-1 ring-on-dark/15">
      <p className="flex items-center gap-2 font-heading text-[11px] font-black uppercase tracking-wider text-on-dark/55">
        <Icon size={13} aria-hidden="true" />
        {label}
      </p>
      <p className="mt-2 font-heading text-sm font-bold leading-snug text-on-dark">
        {value}
      </p>
    </div>
  );
}

/** Hairline-divided list row. Rows are never individually boxed. */
export function DetailRow({
  leading,
  title,
  detail,
  className,
}: {
  leading: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex gap-4 py-4 first:pt-0 last:pb-0", className)}>
      <div className="shrink-0 pt-0.5">{leading}</div>
      <div className="min-w-0">
        <p className="font-heading text-sm font-bold leading-snug text-text">
          {title}
        </p>
        {detail && (
          <p className="mt-1.5 text-sm leading-relaxed text-text-secondary">
            {detail}
          </p>
        )}
      </div>
    </div>
  );
}
